import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, 
  Bell, 
  BellOff,
  Check,
  CheckCheck,
  Clock,
  Package,
  DollarSign,
  FileCheck,
  MessageSquare,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('fleetflow_token')}`,
  'Content-Type': 'application/json'
});

const NotificationsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications?limit=50`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.ok) {
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? {...n, read: true} : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'POST',
        headers: getHeaders()
      });
      if (res.ok) {
        setNotifications(prev => prev.map(n => ({...n, read: true})));
        setUnreadCount(0);
        toast.success('Toutes les notifications marquées comme lues');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getNotificationIcon = (type) => {
    if (type?.includes('mission')) return <Package className="w-5 h-5 text-blue-500" />;
    if (type?.includes('payment')) return <DollarSign className="w-5 h-5 text-emerald-500" />;
    if (type?.includes('kyc')) return <FileCheck className="w-5 h-5 text-purple-500" />;
    if (type?.includes('ticket') || type?.includes('support')) return <MessageSquare className="w-5 h-5 text-amber-500" />;
    if (type?.includes('system')) return <AlertTriangle className="w-5 h-5 text-orange-500" />;
    return <Bell className="w-5 h-5 text-slate-500" />;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
              Notifications
            </h1>
            <p className="text-slate-600 mt-1">
              {unreadCount > 0 
                ? `${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`
                : 'Toutes vos notifications sont lues'
              }
            </p>
          </div>
          {unreadCount > 0 && (
            <Button onClick={markAllAsRead} variant="outline">
              <CheckCheck className="w-4 h-4 mr-2" />
              Tout marquer comme lu
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <Card>
          <CardContent className="p-0">
            {notifications.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <BellOff className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aucune notification</p>
                <p className="text-sm mt-2">Vous recevrez des notifications pour vos missions, paiements et documents KYC</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div 
                    key={notification.id}
                    className={`p-4 flex items-start gap-4 hover:bg-slate-50 transition-colors ${
                      !notification.read ? 'bg-orange-50/50' : ''
                    }`}
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-medium ${!notification.read ? 'text-slate-900' : 'text-slate-700'}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-slate-600 mt-1">
                            {notification.body}
                          </p>
                        </div>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-2"></span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(notification.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {!notification.read && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" />
                            Marquer comme lu
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NotificationsPage;
